import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getStudentCards,
  createStudentCard,
  getStudentCardById,
  deleteStudentCard,
  updateStudentCard, // Import the updateStudentCard function
} from '../services/studentCardService';
import Notification from '../components/Common/Notification';

export const StudentCardContext = createContext();

export const StudentCardProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [studentCards, setStudentCards] = useState([]);
  const [selectedStudentCard, setSelectedStudentCard] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllStudentCards = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStudentCards(authHeaders);
      setStudentCards(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching student cards.';
      showNotification(errorMessage, true);
    }
  };

  const fetchStudentCardById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStudentCardById(id, authHeaders);
      setSelectedStudentCard(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching student card with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addStudentCard = async (studentCardData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newStudentCard = await createStudentCard(studentCardData, authHeaders);
      setStudentCards(prevStudentCards => [...prevStudentCards, newStudentCard]);
      showNotification('Student card added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding student card.';
      showNotification(errorMessage, true);
    }
  };

  const removeStudentCard = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteStudentCard(id, authHeaders);
      setStudentCards(prevStudentCards => prevStudentCards.filter(card => card.id !== id));
      showNotification('Student card removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing student card.';
      showNotification(errorMessage, true);
    }
  };

  // New function to update a student card
  const modifyStudentCard = async (id, updatedData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedStudentCard = await updateStudentCard(id, { ...authHeaders, ...updatedData });
      setStudentCards(prevStudentCards =>
        prevStudentCards.map(card => (card.id === id ? updatedStudentCard : card))
      );
      showNotification('Student card updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error updating student card with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllStudentCards();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <StudentCardContext.Provider
      value={{
        studentCards,
        selectedStudentCard,
        fetchAllStudentCards,
        fetchStudentCardById,
        addStudentCard,
        removeStudentCard,
        modifyStudentCard, // Make the update function available in the context
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </StudentCardContext.Provider>
  );
};

export const useStudentCard = () => useContext(StudentCardContext);
